import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
} from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import {
  addVehicleSchema,
} from "components/Common/validation"
import isPermissionEnabled from "common/isPermissionEnabled"
import {
  getImagePreviewSrc2,
  handleFileChange,
} from "components/Shared/fileHandlers"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import {
  addVehicle,
  getVehicleDetails,
  updateVehicle,
} from "store/actions/vehicleAction"

/** Recommended square logo size for vehicle brands (px) and max upload size (KB). */
const VEHICLE_BRAND_IMAGE_SIZE = 512
const VEHICLE_BRAND_IMAGE_MAX_KB = 500

const initialValues = {
  brand: "",
  brandAr: "",
  image: "",
}

const AddVehicle = ({ mode }) => {
  const {
    t,
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Vehicle "
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, vehicleDetails } = useSelector(
    state => state.Vehicles
  )

  const [formValues, setFormValues] = useState(initialValues)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      35,
      "vehicle_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      35,
      "vehicle_3",
      accountType
    )

    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getVehicleDetails(payload))
  }

  // GET OLD VALUES WHEN HAVE
  useEffect(() => {
    if (vehicleDetails && !loading && mode === "edit") {
      const { _id, brand, brandAr, logo } = vehicleDetails
      setFormValues({
        ...formValues,
        brand,
        brandAr: brandAr || "",
        image: logo,
      })
    }
  }, [vehicleDetails])

  const handleOnSubmit = values => {
    const { brand, brandAr, image } = values

    let data = new FormData()
    data.append("brand", brand)
    data.append("brandAr", brandAr)
    data.append("image", image)
    if (mode === "add") {
      dispatch(addVehicle(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      data.append("id", id)
      dispatch(updateVehicle(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Vehicle")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addVehicleSchema(mode)}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="brand">
                              <span className="text-danger">*</span>{" "}
                              {t("Brand")}
                            </label>
                            <Field
                              type="text"
                              id="brand"
                              name="brand"
                              placeholder={t("Brand")}
                              className={`form-control ${
                                touched.brand && errors.brand
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {/* {console.log(errors)} */}
                            <TextError name="brand" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="brandAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Brand(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="brandAr"
                              name="brandAr"
                              placeholder={t("Brand(عربي)")}
                              className={`form-control ${
                                touched.brandAr && errors.brandAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="brandAr" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field position-relative">
                            <label htmlFor="image">
                              <span className="text-danger">*</span>{" "}
                              {t("Upload Image")}
                            </label>
                            <TranslatedFileInput
                              id="image"
                              name="image"
                              accept="image/jpeg, image/jpg, image/png, image/webp"
                              invalid={!!(touched.image && errors.image)}
                              onChange={e => {
                                handleFileChange(
                                  e,
                                  "image",
                                  setFieldValue,
                                  VEHICLE_BRAND_IMAGE_SIZE,
                                  VEHICLE_BRAND_IMAGE_SIZE,
                                  VEHICLE_BRAND_IMAGE_MAX_KB
                                )
                              }}
                            />
                            <small className="text-muted d-block mt-1">
                              {t(
                                "Please upload a square image with recommended dimensions of {{width}} × {{height}} px.",
                                {
                                  width: VEHICLE_BRAND_IMAGE_SIZE,
                                  height: VEHICLE_BRAND_IMAGE_SIZE,
                                }
                              )}
                            </small>
                            <TextError name="image" />
                          </div>
                          {getImagePreviewSrc2(values.image) && (
                            <div className="mt-3">
                              <img
                                src={getImagePreviewSrc2(values.image)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )}
                        </div>
                        {/* <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="brand">{t("Status")}</label>
                            <Switch
                              onClickHandler={e => {
                                setFieldValue("status", !values.status)
                              }}
                              isChecked={values.status}
                            />
                          </div>
                        </div> */}
                        <div className="mt-3"></div>
                        <div className="col-12 d-flex justify-content-end my-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>

                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddVehicle
